import { useState, useCallback, useRef, useEffect } from 'react';
import { useReactRouterAdapter } from './adapters/reactRouter';
import type { FilterValue, UseUrlParamOptions } from './types';

function buildParamKey(key: string, prefix?: string, separator = '.'): string {
  return prefix ? `${prefix}${separator}${key}` : key;
}

//syncs a single value with a URL search param.
//lighter-weight than useUrlFilters for individual values.
//supports prefix, custom serialization, validation, and configurable history mode.
export function useUrlParam<T extends FilterValue>(
  key: string,
  defaultValue: T,
  options: UseUrlParamOptions<T> = {}
): [T, (value: T) => void] {
  const {
    prefix,
    prefixSeparator = '.',
    replace = true,
    serialize,
    deserialize,
    validate,
  } = options;

  const adapter = useReactRouterAdapter();
  const paramKey = buildParamKey(key, prefix, prefixSeparator);

  const readValueFromUrl = useCallback((): T => {
    const params = adapter.get();
    const isArray = Array.isArray(defaultValue);
    const rawValues = params.getAll(paramKey);

    if (rawValues.length === 0) return defaultValue;

    const raw: string | string[] = isArray ? rawValues : rawValues[0];

    if (deserialize) {
      const value = deserialize(raw);
      if (validate) {
        const validated = validate(value);
        return validated === undefined ? defaultValue : validated;
      }
      return value;
    }

    //default deserialization
    let value: FilterValue;
    if (isArray) {
      value = typeof (defaultValue as any[])[0] === 'number'
        ? (rawValues.map(Number).filter(n => !isNaN(n)) as any)
        : rawValues;
    } else if (typeof defaultValue === 'number') {
      const n = Number(rawValues[0]);
      value = isNaN(n) ? defaultValue : n;
    } else if (typeof defaultValue === 'boolean') {
      value = rawValues[0] === 'true';
    } else {
      value = rawValues[0];
    }

    if (validate) {
      const validated = validate(value as T);
      return validated === undefined ? defaultValue : validated;
    }

    return value as T;
  }, [adapter, paramKey, defaultValue, deserialize, validate]);

  const [value, setValueInternal] = useState<T>(readValueFromUrl);
  const writingRef = useRef(false);

  //sync state -> URL
  useEffect(() => {
    const params = new URLSearchParams(adapter.get());
    const isDefault =
      Array.isArray(value)
        ? JSON.stringify(value) === JSON.stringify(defaultValue)
        : value === defaultValue || value === null || value === '';

    if (isDefault) {
      if (params.has(paramKey)) {
        params.delete(paramKey);
        writingRef.current = true;
        adapter.set(params, { replace });
      }
    } else {
      const serialized = serialize
        ? serialize(value)
        : Array.isArray(value)
          ? value.map(String)
          : String(value);

      if (Array.isArray(serialized)) {
        const current = params.getAll(paramKey);
        if (JSON.stringify(current) !== JSON.stringify(serialized)) {
          params.delete(paramKey);
          serialized.forEach(v => params.append(paramKey, v));
          writingRef.current = true;
          adapter.set(params, { replace });
        }
      } else {
        if (params.get(paramKey) !== serialized) {
          params.set(paramKey, serialized);
          writingRef.current = true;
          adapter.set(params, { replace });
        }
      }
    }
  }, [value]);

  //sync URL -> state (back/forward navigation)
  useEffect(() => {
    if (writingRef.current) {
      writingRef.current = false;
      return;
    }
    const urlValue = readValueFromUrl();
    setValueInternal(prev => {
      if (Array.isArray(prev) && Array.isArray(urlValue)) {
        return JSON.stringify(prev) !== JSON.stringify(urlValue) ? urlValue : prev;
      }
      return prev !== urlValue ? urlValue : prev;
    });
  }, [adapter.get()]);

  const setValue = useCallback((newValue: T) => {
    setValueInternal(newValue);
  }, []);

  return [value, setValue];
}
